import type { Me } from '@mollusk/shared';
import MenuIcon from '@mui/icons-material/Menu';
import AppBar from '@mui/material/AppBar';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import ButtonBase from '@mui/material/ButtonBase';
import Divider from '@mui/material/Divider';
import Drawer from '@mui/material/Drawer';
import IconButton from '@mui/material/IconButton';
import List from '@mui/material/List';
import ListItemButton from '@mui/material/ListItemButton';
import ListItemText from '@mui/material/ListItemText';
import ListSubheader from '@mui/material/ListSubheader';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import Toolbar from '@mui/material/Toolbar';
import { useState } from 'react';
import { ShellMark } from '../../components/ShellMark';
import { type NavItem, navFor } from './nav';

/** Dark top bar (#171): wide screens show links; phones get a drawer. */
export function AppNav({
  me,
  pathname,
  onNavigate,
  onSignOut,
}: {
  me: Me | null | undefined;
  pathname: string;
  onNavigate: (to: string) => void;
  onSignOut: () => void;
}) {
  const [drawer, setDrawer] = useState(false);
  const [adminAnchor, setAdminAnchor] = useState<HTMLElement | null>(null);
  const ready = Boolean(me && !me.needs_terms);
  const nav = me ? navFor(me.role) : { main: [], right: [], admin: [] };
  const go = (i: NavItem) => {
    setDrawer(false);
    setAdminAnchor(null);
    if (i.href) window.location.href = i.href;
    else if (i.to) onNavigate(i.to);
  };
  const active = (i: NavItem) => Boolean(i.to && pathname.startsWith(i.to));
  const linkSx = (i: NavItem) => ({
    color: '#fff',
    opacity: active(i) ? 1 : 0.8,
    borderBottom: active(i) ? '2px solid #0E7C7B' : '2px solid transparent',
    borderRadius: 0,
    '&:hover': { opacity: 1, backgroundColor: 'rgba(255,255,255,0.06)' },
  });

  return (
    <AppBar position="static">
      <Toolbar sx={{ gap: 0.5, px: { xs: 2, sm: 3 } }}>
        <ButtonBase onClick={() => onNavigate('/')} aria-label="mollusk home" sx={{ mr: 2 }}>
          <ShellMark />
        </ButtonBase>
        {ready ? (
          <>
            <Box sx={{ display: { xs: 'none', md: 'flex' }, gap: 0.5, flexGrow: 1 }}>
              {nav.main.map((i) => (
                <Button key={i.label} onClick={() => go(i)} sx={linkSx(i)}>
                  {i.label}
                </Button>
              ))}
              {nav.admin.length > 0 ? (
                <>
                  <Button
                    sx={linkSx({ label: 'Admin' })}
                    onClick={(e) => setAdminAnchor(e.currentTarget)}
                  >
                    Admin
                  </Button>
                  <Menu
                    anchorEl={adminAnchor}
                    open={Boolean(adminAnchor)}
                    onClose={() => setAdminAnchor(null)}
                  >
                    {nav.admin.map((i) => (
                      <MenuItem key={i.label} onClick={() => go(i)}>
                        {i.label}
                      </MenuItem>
                    ))}
                  </Menu>
                </>
              ) : null}
            </Box>
            <Box sx={{ display: { xs: 'none', md: 'flex' }, gap: 0.5 }}>
              {nav.right.map((i) => (
                <Button key={i.label} onClick={() => go(i)} sx={linkSx(i)}>
                  {i.label}
                </Button>
              ))}
              <Button onClick={onSignOut} sx={linkSx({ label: 'Sign out' })}>
                Sign out
              </Button>
            </Box>
            <Box sx={{ flexGrow: 1, display: { md: 'none' } }} />
            <IconButton
              aria-label="Open menu"
              onClick={() => setDrawer(true)}
              sx={{ color: '#fff', display: { md: 'none' } }}
            >
              <MenuIcon />
            </IconButton>
            <Drawer anchor="right" open={drawer} onClose={() => setDrawer(false)}>
              <List sx={{ width: 260 }}>
                {[...nav.main, ...nav.right].map((i) => (
                  <ListItemButton key={i.label} selected={active(i)} onClick={() => go(i)}>
                    <ListItemText primary={i.label} />
                  </ListItemButton>
                ))}
                {nav.admin.length > 0 ? (
                  <>
                    <Divider />
                    <ListSubheader>Admin</ListSubheader>
                    {nav.admin.map((i) => (
                      <ListItemButton key={i.label} onClick={() => go(i)}>
                        <ListItemText primary={i.label} />
                      </ListItemButton>
                    ))}
                  </>
                ) : null}
                <Divider />
                <ListItemButton onClick={onSignOut}>
                  <ListItemText primary="Sign out" />
                </ListItemButton>
              </List>
            </Drawer>
          </>
        ) : me ? (
          <>
            <Box sx={{ flexGrow: 1 }} />
            <Button onClick={onSignOut} sx={{ color: '#fff' }}>
              Sign out
            </Button>
          </>
        ) : null}
      </Toolbar>
    </AppBar>
  );
}
